import React from 'react';
import { getStateIcon } from './PostIcon';
import { PostPolicyAction, usePostPolicyCheck } from './postPolicyCheck';
import type { PostStateBarComponentProps } from './PostStateBar';
import PostStateBarOverflowMenu from './PostStateBarOverflowMenu';
import PostViewStateBar from './PostViewStateBar';
import useToggleArchived from './useToggleArchived';
import useAppSelector from '../app/redux/useAppSelector';
import { MenuItem } from '../components/Menu';
import SVGIcon from '../components/SVGIcon';
import Timestamp from '../components/Timestamp';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { PostType } from '../graphql/generated/graphql';
import unarchiveIcon from '../icons/non-prefilled/16x16/unarchive.svg';
import { useAllUsersMap } from '../user/useAllUsers';
import Link from '../utils/router/Link';
import userPath from '../utils/url/userPath';

const POST_FRAGMENT = gql(`
  fragment PostArchivedBar_post on Post {
    id
    type
    publishedAt
    archivedAt
    archiver {
      id
    }
    ...usePostPolicyCheck_post
    ...useToggleArchived_post
  }  
`);

const getName = (isTemplate: boolean, isDraft: boolean) => {
  if (isTemplate) {
    return isDraft ? 'Draft Template' : 'Template';
  }
  return isDraft ? 'Draft' : 'Archived';
};

interface PostArchivedBarProps extends PostStateBarComponentProps {
  post: DocumentType<typeof POST_FRAGMENT>;
}

const PostArchivedBar = ({ post, compact }: PostArchivedBarProps) => {
  const postPolicyCheck = usePostPolicyCheck();
  const currentUser = useAppSelector((state) => state.session.user);
  const toggleArchived = useToggleArchived();
  const allUsersMap = useAllUsersMap({ includeDeactivated: true });

  const archiver = post.archiver ? allUsersMap[post.archiver.id] : null;
  if (!archiver) return null;

  const isTemplate = post ? post.type === PostType.Template : false;
  const isDraft = post ? !post.publishedAt : false;

  return (
    <PostViewStateBar
      actions={
        postPolicyCheck(PostPolicyAction.EditPost, post, currentUser) && (
          <PostStateBarOverflowMenu variant="muted">
            <MenuItem icon={unarchiveIcon} onClick={() => toggleArchived(post)}>
              Unarchive
            </MenuItem>
          </PostStateBarOverflowMenu>
        )
      }
    >
      <SVGIcon>
        {getStateIcon({
          type: post.type,
          draft: isDraft,
          archived: true,
        })}
      </SVGIcon>
      <span>
        <strong>{getName(isTemplate, isDraft)}</strong>
        {!compact && (
          <>
            {isTemplate || isDraft ? ' archived by ' : ' by '}
            <Link to={userPath(archiver)}>
              {currentUser.id === archiver.id ? 'you' : archiver.name}
            </Link>{' '}
            <Timestamp date={post.archivedAt} />
          </>
        )}
      </span>
    </PostViewStateBar>
  );
};

export default PostArchivedBar;
